import React, { FC, useCallback, useState } from 'react';
import firebase from 'firebase/app';
import { adminApi } from 'util/adminApi';
import { serverQuery } from 'util/serverRead';
import { ui, bucketFrame, button } from 'util/ui';
import SectionHeader from 'components/shell/SectionHeader';
import { Panel, TRow, Cell, AdminButton, Pill, Notice } from './adminUi';
import { Entry, toEntry, describe, actorName, canRestore } from './ActivityLog';

/**
 * Project › History (Phase 2, 5 Nov 2026; admins only, like the activity log it reads).
 * Every change to this project and everything in it, newest first, from the server's activity log:
 * the project itself, its records, notes, assets, gallery images, and the video files in its records.
 * A deleted item can be put back from its saved copy, as on Admin › Activity.
 *
 * The log keeps a copy of each item before and after every change. Records, notes, assets and
 * gallery items carry their project's ID in that copy; videos carry their record's ID. So the
 * history is found with a few read-only queries and no new indexes. It goes back to when the log
 * started (5 Nov 2026 on live).
 */

const SHOW = 50;
function chunk<T>(xs: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out;
}

const ProjectHistory: FC<{ projectId: string; recordIds: string[] }> = ({ projectId, recordIds }) => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [shown, setShown] = useState(SHOW);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const log = firebase.firestore().collection('activity');
      const found = new Map<string, Entry>();
      const add = (s: firebase.firestore.QuerySnapshot) => s.docs.forEach((d) => found.set(d.id, toEntry(d)));

      (
        await Promise.all([
          serverQuery(log.where('collection', '==', 'projects').where('docId', '==', projectId)),
          serverQuery(log.where('after.projectID', '==', projectId)),
          serverQuery(log.where('before.projectID', '==', projectId)),
        ])
      ).forEach(add);

      // Video files belong to records: every record in the project now, plus any the log shows were in it.
      const records = new Set(recordIds);
      found.forEach((e) => e.collection === 'records' && e.docId && records.add(e.docId));
      const groups = chunk(Array.from(records), 10); // "in" takes up to 10 values
      (
        await Promise.all(
          groups.reduce(
            (qs, ids) => qs.concat([serverQuery(log.where('after.recordID', 'in', ids)), serverQuery(log.where('before.recordID', 'in', ids))]),
            [] as Promise<firebase.firestore.QuerySnapshot>[],
          ),
        )
      ).forEach(add);

      const list = Array.from(found.values()).sort((a, b) => (b.at ? b.at.getTime() : Infinity) - (a.at ? a.at.getTime() : Infinity));
      setEntries(list);
      try {
        const users = await adminApi.listUsers();
        setEmails(Object.fromEntries(users.users.map((u) => [u.uid, u.email || u.uid])));
      } catch {
        // names are a nicety; the history still shows
      }
    } catch (e) {
      setMessage(`Couldn't load the history: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [projectId, recordIds]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  const restore = async (e: Entry) => {
    if (!window.confirm(`Put back ${describe(e).replace(/^Deleted /, '')} exactly as it was before it was deleted?`)) return;
    setBusy(e.id);
    try {
      const res = await adminApi.restoreDeleted(e.id);
      setMessage(`Restored ${res.restored}.${res.note ? ' ' + res.note : ''}`);
      await load();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const COLS = '150px minmax(0,3fr) minmax(0,1.5fr) 110px';
  return (
    <div style={{ ...bucketFrame, fontFamily: ui.font, background: ui.surface }}>
      <SectionHeader title="History" count={entries ? entries.length : undefined}>
        <button
          type="button"
          className="archy-btn archy-onbucket"
          aria-expanded={open}
          onClick={toggle}
          style={{
            height: 34,
            padding: '0 12px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.55)',
            background: 'rgba(255,255,255,0.12)',
            color: '#FFFFFF',
            font: 'inherit',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {open ? 'Hide' : 'Show history'}
        </button>
      </SectionHeader>
      {open && (
        <div style={{ padding: 12 }}>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: ui.muted }}>
            Every change to this project and its records, files, notes and gallery since the activity log started. Only admins see this.
          </p>
          {message && <Notice>{message}</Notice>}
          {loading && !entries && <p style={{ margin: 0, fontSize: 14, color: ui.muted }}>Loading…</p>}
          {entries && entries.length === 0 && (
            <p style={{ margin: 0, fontSize: 14, color: ui.muted }}>No changes recorded for this project yet.</p>
          )}
          {entries && entries.length > 0 && (
            <Panel>
              <TRow head columns={COLS}>
                <Cell>When</Cell>
                <Cell>What</Cell>
                <Cell>Who</Cell>
                <Cell />
              </TRow>
              {entries.slice(0, shown).map((e) => (
                <TRow key={e.id} columns={COLS}>
                  <Cell muted>{e.at ? e.at.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' } as any) : '…'}</Cell>
                  <Cell>{describe(e)}</Cell>
                  <Cell muted label="Who" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {actorName(e, emails)}
                  </Cell>
                  <Cell>
                    {canRestore(e) && (
                      <AdminButton tone="primary" onClick={() => restore(e)} disabled={busy !== null}>
                        {busy === e.id ? 'Restoring…' : 'Restore'}
                      </AdminButton>
                    )}
                    {!!e.restoredAt && <Pill tone="green">Restored</Pill>}
                  </Cell>
                </TRow>
              ))}
              {entries.length > shown && (
                <div style={{ padding: '10px 16px', borderTop: `1px solid ${ui.lineSoft}` }}>
                  <button type="button" className="archy-btn" style={{ ...button('plain'), height: 36 }} onClick={() => setShown(shown + SHOW)}>
                    Show {Math.min(SHOW, entries.length - shown)} more
                  </button>
                </div>
              )}
            </Panel>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectHistory;
